import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  Skeleton,
  Stack,
} from '@chakra-ui/react'
import type { DetailedUser, EditUser } from 'BrokerTsLib/src/dtos'
import { useGetRoles, useGetUserTypes } from 'BrokerTsLib/src/hooks/useUser'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import type { Ref } from 'react'
import { forwardRef, useEffect, useMemo } from 'react'

import { FormGroup } from '@/components/FormGroup'
import { SwitchInput } from '@/components/SwitchInput'
import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'

export const UserAccessFormSection = forwardRef(function UserAccessFormSection(
  {
    user,
    isCreate,
    ...rest
  }: { user: DetailedUser; isCreate: boolean } & FormSectionProps,
  ref: Ref<HTMLDivElement>,
) {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext<EditUser>()

  const { data, isLoading: isLoadingTypes } = useGetUserTypes()

  return (
    <FormSection ref={ref} sectionTitle="Nastavení přístupu" {...rest}>
      <Stack>
        <FormGroup sx={{ columnGap: 4, alignItems: 'flex-start' }}>
          <RoleFormControl isCreate={isCreate} user={user} />

          <FormControl variant="fullWidth" isRequired>
            <FormLabel>Typ uživatele</FormLabel>

            <Skeleton isLoaded={!isLoadingTypes}>
              <Select
                {...register('userTypeId', {
                  required: 'Vyberte typ uživatele',
                })}
              >
                {isCreate && <option value={''}>Vyberte typ uživatele</option>}
                {data?.data.map(({ id, itemName }, i) => (
                  <option key={i} value={id}>
                    {itemName}
                  </option>
                ))}
              </Select>
            </Skeleton>
            <FormErrorMessage>{errors.roles?.message}</FormErrorMessage>
          </FormControl>

          {!isCreate && (
            <FormControl mb={3} alignSelf="end">
              <SwitchInput {...register('valid')}>Aktivní</SwitchInput>
            </FormControl>
          )}
        </FormGroup>
      </Stack>
    </FormSection>
  )
})

const RoleFormControl = ({
  isCreate,
  user,
}: {
  isCreate: boolean
  user: DetailedUser
}) => {
  const {
    register,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext<EditUser>()
  const { data: rolesData, isLoading: loadingRoles } = useGetRoles(user.userId)
  const Roles = useMemo(() => rolesData?.data ?? [], [rolesData])

  const role = watch('roles.0.role', undefined)
  useEffect(() => {
    if (role)
      setValue('roles.0', {
        role,
        roleName: Roles.find((x) => x.role === role)?.roleName ?? '',
      })
  }, [role, Roles])

  return (
    <FormControl variant="fullWidth" isRequired isInvalid={!!errors.roles}>
      <FormLabel>Role</FormLabel>

      <Skeleton minW={'28'} isLoaded={!loadingRoles}>
        <Select
          isInvalid={!!errors.roles}
          {...register('roles.0.role', {
            validate: (value) => (value ? undefined : 'Vyberte roli'),
            valueAsNumber: true,
          })}
        >
          {isCreate && <option value={0}>Vyberte roli</option>}
          {Roles.map((role) => (
            <option key={role.role} value={role.role}>
              {role.roleName}
            </option>
          ))}
        </Select>
      </Skeleton>

      <FormErrorMessage>
        {errors.roles?.at?.(0)?.role?.message}
      </FormErrorMessage>
    </FormControl>
  )
}
